import { useEffect, useState } from "react";
import { Activity, ArrowRight, BookOpen, Database, FileText, Globe, Hourglass, KeyRound, LifeBuoy, Search, Shield, Sparkles, UserX, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { EmailingOverview, getEmailingOverview } from "interfaces/emailing";

/**
 * /bo/users — home of the « Utilisateurs » menu, built like /bo: a few live counts that link into
 * the matching email segment, then every user-related page grouped by what you came to do.
 */
const sections = [
    {
        title: "Support",
        description: "Le chat en bas à droite de l'application : messages et centre d'aide.",
        icon: LifeBuoy,
        links: [
            { label: "Tickets support", path: "/bo/support", icon: LifeBuoy },
            { label: "Centre d'aide", path: "/bo/help-center", icon: BookOpen },
        ],
    },
    {
        title: "Usage",
        description: "Ce que les utilisateurs font dans l'application.",
        icon: Activity,
        links: [
            { label: "Activité des utilisateurs", path: "/bo/activity", icon: Activity },
            { label: "Historique des recherches", path: "/bo/search-logs", icon: Search },
            { label: "Prompts IA", path: "/bo/ai-prompts", icon: Sparkles },
        ],
    },
    {
        title: "API",
        description: "Les clients qui consomment l'API publique et leurs clés.",
        icon: Globe,
        links: [
            { label: "API publique", path: "/bo/public-api", icon: Globe },
            { label: "Clés API", path: "/bo/api-keys", icon: KeyRound },
        ],
    },
    {
        title: "Technique",
        description: "Journaux et cache côté serveur.",
        icon: Shield,
        links: [
            { label: "Journaux techniques", path: "/bo/query-logs", icon: FileText },
            { label: "Cache", path: "/bo/cache", icon: Database },
        ],
    },
];

export default function UsersHub() {
    const [overview, setOverview] = useState<EmailingOverview | null>(null);

    useEffect(() => {
        getEmailingOverview(30).then(setOverview).catch(() => setOverview(null));
    }, []);

    const u = overview?.users;
    const stats = [
        { label: "Comptes", value: u?.all, hint: overview ? `+${overview.newUsers} sur 30 jours` : "", path: "/bo/emailing", icon: Users },
        { label: "Clients premium", value: u?.customers, hint: "Abonnés", path: "/bo/emailing?segment=customers", icon: Shield },
        { label: "Essai finit sous 7 j", value: u?.trial_ending, hint: "À convertir maintenant", path: "/bo/emailing?segment=trial_ending", icon: Hourglass },
        { label: "Inactifs 30 j", value: u?.inactive, hint: "À relancer", path: "/bo/emailing?segment=inactive", icon: UserX },
    ];

    return (
        <div className="min-h-full bg-[#f5f7fb] px-5 py-10 md:px-10 md:py-14">
            <div className="mx-auto max-w-6xl">
                <header className="mb-9">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#68738d]">Back-office</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-[#17244a] md:text-4xl">Utilisateurs</h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-[#68738d]">
                        Les comptes de l'application : qui ils sont, ce qu'ils font, et les emails qu'on leur envoie.
                    </p>
                </header>

                <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {stats.map(({ label, value, hint, path, icon: Icon }) => (
                        <Link
                            key={label}
                            to={path}
                            className="group flex items-center gap-4 rounded-2xl border border-[#e3e8f1] bg-white p-5 shadow-[0_2px_10px_rgba(20,32,64,0.04)] transition-colors hover:border-[#cfd6e4]"
                        >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1f4fa] text-[#273b6b]">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-[#68738d]">{label}</p>
                                <p className="text-2xl font-semibold text-[#17244a]">{value ?? "—"}</p>
                                {hint && <p className="truncate text-xs text-[#68738d]">{hint}</p>}
                            </div>
                            <ArrowRight className="h-4 w-4 shrink-0 text-[#7a849b] opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                    ))}
                </div>

                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {sections.map((section) => {
                        const SectionIcon = section.icon;
                        return (
                            <section key={section.title} className="flex flex-col rounded-2xl border border-[#e3e8f1] bg-white p-5 shadow-[0_2px_10px_rgba(20,32,64,0.04)]">
                                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#f1f4fa] text-[#273b6b]">
                                    <SectionIcon className="h-5 w-5" />
                                </div>
                                <h2 className="text-base font-semibold text-[#17244a]">{section.title}</h2>
                                <p className="mt-1 min-h-[60px] text-xs leading-5 text-[#68738d]">{section.description}</p>
                                <nav aria-label={section.title} className="mt-3 space-y-1">
                                    {section.links.map(({ label, path, icon: Icon }) => (
                                        <Link
                                            key={path}
                                            to={path}
                                            className="group flex items-center gap-2 rounded-lg px-2 py-2 text-xs font-medium text-[#35415f] transition-colors hover:bg-[#f5f7fb] hover:text-[#17244a]"
                                        >
                                            <Icon className="h-4 w-4 shrink-0 text-[#7a849b]" />
                                            <span className="min-w-0 flex-1">{label}</span>
                                            <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                                        </Link>
                                    ))}
                                </nav>
                            </section>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
